import dayjs from "dayjs";

/*
 * Everything the dashboard derives from one API response.
 *
 * Kept out of the component on purpose: these are the page's actual claims —
 * who is at their desk, who started late, who never started — and they are
 * worth reading and checking on their own rather than buried in JSX.
 *
 * The source is GET /api/usage/all-users-activity-dashboard, whose per-user
 * shape is:
 *
 *   { _id, name, userEmail, role, team, isActive,
 *     firstTimeEntry, lastTimeEntry, lastActivity,   // ISO strings or null
 *     totalWorkTime, totalIdleTime,                  // milliseconds
 *     productivityBreakdown: { productive, neutral, unproductive, unrated } }
 *
 * Note `totalWorkTime` is MILLISECONDS here, while the markup's mock data was in
 * minutes. Getting that wrong turns 8 hours into 8 minutes, so every consumer
 * below goes through the ms helpers in lib/format.js.
 */

/** Idle thresholds for the "currently working" grid, from the markup's legend. */
const ACTIVE_WITHIN_MS = 5 * 60 * 1000;
const WAITING_WITHIN_MS = 10 * 60 * 1000;

/**
 * active  — input in the last 5 minutes
 * waiting — 5–10 minutes
 * away    — 10+ minutes
 *
 * `now` is a parameter rather than Date.now() so this is deterministic to test
 * and so every row on one render is measured against the same instant.
 */
export function presenceOf(user, now = Date.now()) {
    if (!user.lastActivity) return null; // never started — not on this grid at all

    const since = now - new Date(user.lastActivity).getTime();
    if (since <= ACTIVE_WITHIN_MS) return "active";
    if (since <= WAITING_WITHIN_MS) return "waiting";
    return "away";
}

/**
 * Did this person start after the organization's cutoff?
 *
 * `lateStartTime` is "HH:mm" in the ORGANIZATION's working day, and
 * firstTimeEntry is an ISO instant. Comparing them means comparing wall-clock
 * times, which is why this compares hours and minutes rather than timestamps —
 * a UTC comparison would call everyone in Dhaka late.
 */
export function isLateStart(user, lateStartTime = "09:05") {
    if (!user.firstTimeEntry) return false;

    const [h, m] = String(lateStartTime).split(":").map(Number);
    if (!Number.isInteger(h) || !Number.isInteger(m)) return false;

    const start = dayjs(user.firstTimeEntry);
    return start.hour() * 60 + start.minute() > h * 60 + m;
}

/** Percentage of a user's tracked app time rated productive. */
export function productivityOf(user) {
    const b = user.productivityBreakdown;
    if (!b) return 0;

    const total = b.productive + b.neutral + b.unproductive + b.unrated;
    if (!total) return 0;
    return Math.round((b.productive / total) * 100);
}

/**
 * One pass over the users array producing every list the page shows.
 *
 * One pass rather than four filters because the lists are defined against each
 * other — "didn't start" is precisely "not in any of the others" — and deriving
 * them independently is how they drift apart and stop adding up to the team.
 */
export function deriveDashboard(payload, now = Date.now()) {
    const users = payload?.users ?? [];
    const lateStartTime = payload?.lateStartTime || "09:05";

    const working = [];
    const late = [];
    const notStarted = [];
    const rows = [];

    for (const user of users) {
        const started = Boolean(user.firstTimeEntry);
        const presence = presenceOf(user, now);
        const late_ = isLateStart(user, lateStartTime);

        if (presence) working.push({ ...user, presence });
        if (started && late_) late.push(user);
        if (!started) notStarted.push(user);

        rows.push({
            id: user._id,
            name: user.name,
            email: user.userEmail,
            team: user.team || null,
            start: user.firstTimeEntry,
            late: late_,
            started,
            workMs: user.totalWorkTime || 0,
            idleMs: user.totalIdleTime || 0,
            // Share of TRACKED time spent idle. A big work number beside a big
            // idle number does not answer "is this day going badly"; this does.
            idlePct:
                (user.totalWorkTime || 0) + (user.totalIdleTime || 0) > 0
                    ? ((user.totalIdleTime || 0) /
                          ((user.totalWorkTime || 0) + (user.totalIdleTime || 0))) *
                      100
                    : 0,
            productivity: productivityOf(user),
            presence,
        });
    }

    // Latest starters first: the person who came in at 10:02 is the one an
    // admin wants to see, not the one who was six minutes late.
    late.sort((a, b) => new Date(b.firstTimeEntry) - new Date(a.firstTimeEntry));
    notStarted.sort((a, b) => a.name.localeCompare(b.name));

    const totalWorkMs = payload?.totalWorkTime || 0;
    const totalIdleMs = payload?.totalIdleTime || 0;
    const trackedMs = totalWorkMs + totalIdleMs;

    return {
        users,
        rows,
        working,
        late,
        notStarted,
        lateStartTime,

        // "Absent from desk" is the away bucket — present but no input for 10+
        // minutes. Distinct from "didn't start", which is people with no time
        // entry at all; the two cards would otherwise double-count.
        awayCount: working.filter((u) => u.presence === "away").length,

        attended: users.length - notStarted.length,
        teamSize: users.length,

        totalWorkMs,
        totalIdleMs,
        // Guard the divide: an organization with no attendance yet would
        // otherwise show NaN in two cards.
        avgWorkMs: users.length - notStarted.length > 0
            ? Math.round(totalWorkMs / (users.length - notStarted.length))
            : 0,
        idleShare: trackedMs > 0 ? Math.round((totalIdleMs / trackedMs) * 1000) / 10 : 0,
        averageProductivity: payload?.averageProductivity ?? 0,
    };
}

/** Date presets for the filter bar, as [start, end] YYYY-MM-DD. */
export const DATE_PRESETS = {
    today: { label: "Today", days: 0 },
    "7d": { label: "7 days", days: 6 },
    "30d": { label: "30 days", days: 29 },
};

export function rangeFor(preset) {
    const days = DATE_PRESETS[preset]?.days ?? 0;
    const end = dayjs();
    return {
        startDate: end.subtract(days, "day").format("YYYY-MM-DD"),
        endDate: end.format("YYYY-MM-DD"),
    };
}
